import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtQty } from "../lib/api";
import { native } from "../lib/native";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Bot, Play, Square, Trash2, Plus, Sparkles, TrendingUp, Grid3x3, Repeat, X, Share2, Brain, Copy, Scale, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import TierCelebration from "../components/TierCelebration";
import CompareModePanel from "../components/CompareModePanel";

const STRATEGIES = [
  { v: "dca",      label: "DCA",      icon: Repeat,     blurb: "Buy a fixed $ of one asset every N seconds. Simple and disciplined." },
  { v: "momentum", label: "Momentum", icon: TrendingUp, blurb: "Trade the strongest 24h mover in your basket above a threshold." },
  { v: "grid",     label: "Grid",     icon: Grid3x3,    blurb: "Buy on X% dip / sell on X% rise vs each asset's last anchor." },
  { v: "ai",       label: "AI Coach",  icon: Sparkles,   blurb: "Claude Sonnet 5 picks one trade per cycle grounded in trading classics." },
];

const CRYPTO_SEED = ["bitcoin", "ethereum", "solana", "cardano", "chainlink"];
const STOCK_SEED = ["AAPL", "MSFT", "GOOGL", "TSLA", "NVDA"];

const PATTERNS = [
  { v: "Bullish Engulfing", kind: "bull" },
  { v: "Bearish Engulfing", kind: "bear" },
  { v: "Hammer",            kind: "bull" },
  { v: "Shooting Star",     kind: "bear" },
  { v: "Doji",              kind: "neutral" },
  { v: "Morning Star",      kind: "bull" },
  { v: "Evening Star",      kind: "bear" },
  { v: "Three White Soldiers", kind: "bull" },
  { v: "Three Black Crows", kind: "bear" },
  { v: "ABCD (bull)",       kind: "bull" },
  { v: "ABCD (bear)",       kind: "bear" },
  { v: "Gartley",           kind: "neutral" },
  { v: "Butterfly",         kind: "neutral" },
  { v: "Bat",               kind: "neutral" },
  { v: "Crab",              kind: "neutral" },
];

const STATUS_COLOR = {
  running: "bg-[hsl(var(--gain))]/15 gain border-[hsl(var(--gain))]/30",
  completed: "bg-secondary text-muted-foreground",
  stopped: "bg-secondary text-muted-foreground",
  error: "bg-[hsl(var(--loss))]/15 loss border-[hsl(var(--loss))]/30",
};

export default function Bots() {
  const [bots, setBots] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [logs, setLogs] = useState({});
  const [stops, setStops] = useState([]);
  const [celebration, setCelebration] = useState(null);   // {tier, botName}
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [bRes, sRes] = await Promise.allSettled([api.get("/bots"), api.get("/stops")]);
    if (bRes.status === "fulfilled") setBots(bRes.value.data);
    if (sRes.status === "fulfilled") setStops(sRes.value.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tier level-up watcher: for each public bot, poll /bots/:id/tier every 30s
  // and compare against localStorage. On level-up, fire the celebration once.
  const TIER_ORDER = ["none", "bronze", "silver", "gold", "platinum"];
  useEffect(() => {
    const publicBots = bots.filter((b) => b.public);
    if (publicBots.length === 0) return;
    let cancelled = false;
    const check = async () => {
      for (const b of publicBots) {
        try {
          const { data } = await api.get(`/bots/${b.id}/tier`);
          if (cancelled) return;
          const currentTier = data.tier || "none";
          const storageKey = `tier:${b.id}`;
          const prevTier = localStorage.getItem(storageKey) || "none";
          const prevIdx = TIER_ORDER.indexOf(prevTier);
          const currIdx = TIER_ORDER.indexOf(currentTier);
          if (currIdx > prevIdx && currentTier !== "none") {
            setCelebration({ tier: currentTier, botName: b.name });
          }
          localStorage.setItem(storageKey, currentTier);
        } catch { /* silent */ }
      }
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bots.map((b) => `${b.id}:${b.public}`).join(",")]);

  useEffect(() => {
    const anyRunning = bots.some((b) => b.status === "running");
    if (!anyRunning && !selectedBot) return;
    const iv = setInterval(async () => {
      load();
      if (selectedBot) {
        try {
          const { data } = await api.get(`/bots/${selectedBot.id}`);
          setSelectedBot(data);
          setLogs((prev) => ({ ...prev, [data.id]: data.log }));
        } catch {}
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [bots, selectedBot, load]);

  const stopBot = async (id) => {
    await api.post(`/bots/${id}/stop`);
    toast.success("Bot stopped");
    load();
  };
  const deleteBot = async (id) => {
    await api.delete(`/bots/${id}`);
    toast.success("Bot deleted");
    setSelectedBot(null);
    load();
  };

  const cloneBot = async (b) => {
    try {
      const { data } = await api.post(`/bots/${b.id}/clone`, { start: true });
      toast.success(`Cloned as "${data.name}"`, {
        description: "Same strategy, symbols and safety settings. New starting equity anchor.",
        duration: 6000,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  const publishBot = async (b) => {
    if (b.public) {
      // Already public → toggle off
      if (!window.confirm(`Unpublish "${b.name}"? It will disappear from the community leaderboard.`)) return;
      try {
        await api.post(`/bots/${b.id}/publish`, { public: false });
        toast.success("Bot unpublished");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
      return;
    }
    const tagline = window.prompt(`Short tagline (max 140 chars, optional):`, "");
    if (tagline === null) return;
    const feeStr = window.prompt(`Signal fee % (0-50) — earn a cut when followers profit. Enter 0 for none:`, "10");
    if (feeStr === null) return;
    const fee = parseFloat(feeStr);
    try {
      await api.post(`/bots/${b.id}/publish`, {
        public: true,
        tagline: tagline || null,
        signal_fee_pct: isNaN(fee) ? 0 : fee,
      });
      toast.success("Published to leaderboard with fresh snapshot", {
        action: { label: "See it live", onClick: () => navigate("/copy") },
        duration: 8000,
      });
      // Offer native OS share sheet with a deep-link to the bot's postmortem
      const shareUrl = `${window.location.origin}/bots/${b.id}/postmortem`;
      native.share({
        title: `Follow ${b.name} on Aanar`,
        text: `${tagline || b.name} — verified track record on Aanar`,
        url: shareUrl,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  const progress = (b) => {
    if (b.duration_type === "count" && b.duration_value) return Math.min(100, (b.trades_placed / b.duration_value) * 100);
    if (b.duration_type === "time" && b.started_at) {
      const elapsed = (Date.now() - new Date(b.started_at)) / 1000;
      return Math.min(100, (elapsed / b.duration_value) * 100);
    }
    return b.status === "running" ? 50 : 100;
  };

  return (
    <div className="space-y-6" data-testid="bots-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><Bot className="w-3 h-3" /> Auto-trade</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Trading bots</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Run a strategy hands-free. Every fill is written to your Orders like a manual trade,
            so P&L, portfolio, and (when routed via IBKR) broker analytics stay accurate.
          </p>
        </div>
        <Button onClick={() => setShowBuilder(true)} className="rounded-full" data-testid="new-bot-btn">
          <Plus className="w-4 h-4 mr-2" /> New bot
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bot list */}
        <div className="lg:col-span-2 space-y-3">
          {bots.length === 0 && (
            <Card className="p-16 text-center border-border rounded-xl">
              <Bot className="w-8 h-8 mx-auto text-muted-foreground opacity-40" />
              <div className="text-sm text-muted-foreground mt-3">No bots yet. Spin one up with the "New bot" button.</div>
            </Card>
          )}
          {bots.map((b) => (
            <Card key={b.id}
                  onClick={() => setSelectedBot(b)}
                  data-testid={`bot-card-${b.id}`}
                  className={`p-4 border-border rounded-xl cursor-pointer transition-colors ${selectedBot?.id === b.id ? "border-foreground/30" : ""}`}>
              {b.public && <TierProgress botId={b.id} />}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold truncate">{b.name}</div>
                    <Badge variant="outline" className={`rounded-full uppercase text-[10px] ${STATUS_COLOR[b.status] || ""}`}
                           data-testid={`bot-status-${b.id}`}>
                      {b.status}
                    </Badge>
                    <Badge variant="secondary" className="rounded-full uppercase text-[10px]">{b.strategy}</Badge>
                    <Badge variant="secondary" className="rounded-full uppercase text-[10px]">{b.route.replace("_", " ")}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {b.trades_placed} trade{b.trades_placed === 1 ? "" : "s"} · spent {fmtMoney(b.spent || 0)}
                    {b.duration_type === "count" && ` · target ${b.duration_value}`}
                    {b.duration_type === "time" && ` · ${b.duration_value}s window`}
                  </div>
                  {b.status === "completed" && (
                    <div className="mt-1.5 text-[11px] p-1.5 rounded-md bg-secondary/40 border border-border/40 text-muted-foreground" data-testid={`bot-completed-${b.id}`}>
                      <span className="font-semibold text-foreground">Bot finished.</span>{" "}
                      {b.duration_type === "count" && `Hit its ${b.duration_value}-trade limit. `}
                      {b.duration_type === "time" && `Reached its ${b.duration_value}s window. `}
                      Tap the copy icon to clone with fresh settings, or trash to remove.
                    </div>
                  )}
                  {b.status === "stopped" && (
                    <div className="mt-1.5 text-[11px] p-1.5 rounded-md bg-secondary/40 border border-border/40 text-muted-foreground" data-testid={`bot-stopped-${b.id}`}>
                      <span className="font-semibold text-foreground">Stopped manually.</span> Delete or create a fresh bot to keep trading.
                    </div>
                  )}
                  <Progress value={progress(b)} className="mt-2 h-1" />
                </div>
                <div className="flex gap-2">
                  {b.public && (
                    <Badge variant="secondary" className="rounded-full text-[10px]" data-testid={`bot-public-${b.id}`}>
                      public
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/bots/${b.id}`); }}
                          className="rounded-full" data-testid={`dashboard-bot-${b.id}`} title="Open bot dashboard">
                    <BarChart3 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); cloneBot(b); }}
                          className="rounded-full" data-testid={`clone-bot-${b.id}`} title="Clone this bot's config into a fresh run">
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); publishBot(b); }}
                          className="rounded-full" data-testid={`publish-bot-${b.id}`} title={b.public ? "Manage sharing" : "Publish to leaderboard"}>
                    <Share2 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/bots/${b.id}/postmortem`); }}
                          className="rounded-full" data-testid={`postmortem-bot-${b.id}`} title="View decision log">
                    <Brain className="w-3 h-3" />
                  </Button>
                  {b.status === "running" ? (
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); stopBot(b.id); }}
                            className="rounded-full" data-testid={`stop-bot-${b.id}`}>
                      <Square className="w-3 h-3 mr-1" /> Stop
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteBot(b.id); }}
                            className="rounded-full text-muted-foreground" data-testid={`delete-bot-${b.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Selected bot detail */}
        <div>
          {selectedBot ? (
            <Card className="p-5 border-border rounded-xl sticky top-4" data-testid="bot-detail">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-semibold">{selectedBot.name}</div>
                <button onClick={() => setSelectedBot(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                <Stat label="Trades" value={selectedBot.trades_placed} />
                <Stat label="Spent" value={fmtMoney(selectedBot.spent || 0)} />
                <Stat label="Strategy" value={selectedBot.strategy} />
                <Stat label="Route" value={selectedBot.route} />
                <Stat label="Interval" value={`${selectedBot.interval_seconds}s`} />
                <Stat label="Status" value={selectedBot.status} />
              </div>

              <CompareModePanel botId={selectedBot.id} />

              {/* Active exit rules — SL/TP stops this bot has attached to open positions */}
              {(() => {
                const botStops = stops.filter((s) => s.status === "active" && s.bot_id === selectedBot.id);
                if (botStops.length === 0) {
                  const sl = (selectedBot.safety || {}).stop_loss_pct;
                  const tp = (selectedBot.safety || {}).take_profit_pct;
                  return (
                    <div className="mb-4 p-2 rounded-lg bg-secondary/30 border border-border/40 text-[11px] text-muted-foreground" data-testid="bot-exit-rules-empty">
                      Exit rules: {sl ? `${(selectedBot.safety || {}).trailing_stop ? "Trailing " : ""}${sl}%` : "no SL"} · {tp ? `TP ${tp}%` : "no TP"} · applied on the next buy.
                    </div>
                  );
                }
                return (
                  <div className="mb-4" data-testid="bot-exit-rules">
                    <div className="text-[10px] font-mono-tab uppercase tracking-wider text-muted-foreground mb-1.5">
                      Active exit rules ({botStops.length})
                    </div>
                    <div className="rounded-lg border border-border/40 divide-y divide-border/40 overflow-hidden">
                      {botStops.slice(0, 6).map((s) => {
                        const isTrail = s.kind === "trailing_stop";
                        return (
                          <div key={s.id} className="px-2.5 py-1.5 text-[11px] font-mono-tab flex items-center justify-between gap-2" data-testid={`bot-stop-${s.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-semibold">{s.ticker || s.symbol}</span>
                              <span className="text-muted-foreground text-[10px]">{fmtQty(s.quantity)}</span>
                              {isTrail && (
                                <span className="px-1 py-0 rounded text-[9px] uppercase tracking-wider bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))]"
                                      title={`Peak $${s.peak_price} · trails ${s.trail_pct}% below`}>
                                  Trail
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              {s.stop_price != null && (
                                <span className="loss">
                                  {isTrail ? "Trail" : "SL"} {fmtMoney(s.stop_price)}
                                  {isTrail && s.peak_price && (
                                    <span className="opacity-60 ml-1">← peak {fmtMoney(s.peak_price)}</span>
                                  )}
                                </span>
                              )}
                              {s.take_price != null && (
                                <span className="gain">TP {fmtMoney(s.take_price)}</span>
                              )}
                              <button onClick={async () => {
                                try { await api.delete(`/stops/${s.id}`); toast.success("Cancelled"); load(); }
                                catch { toast.error("Failed"); }
                              }} data-testid={`bot-stop-cancel-${s.id}`}
                                      className="text-muted-foreground hover:loss text-[10px] underline">
                                cancel
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div className="text-xs uppercase text-muted-foreground mb-2">Activity log</div>
              <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1" data-testid="bot-log">
                {(logs[selectedBot.id] || selectedBot.log || []).slice().reverse().map((e, i) => {
                  const d = new Date(e.t);
                  const now = new Date();
                  const isToday = d.toDateString() === now.toDateString();
                  const yest = new Date(now); yest.setDate(now.getDate() - 1);
                  const isYesterday = d.toDateString() === yest.toDateString();
                  const datePart = isToday ? "Today"
                    : isYesterday ? "Yesterday"
                    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
                  const timePart = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                  return (
                    <div key={i} className="flex gap-2 text-[11px] font-mono-tab" data-testid={`bot-log-row-${i}`}>
                      <span className="text-muted-foreground shrink-0 tabular-nums"
                            title={d.toLocaleString()}>
                        <span className="opacity-60">{datePart}</span> {timePart}
                      </span>
                      <span className={
                        e.level === "trade" ? "gain" :
                        e.level === "warn" ? "text-[hsl(var(--ai))]" :
                        e.level === "error" ? "loss" : "text-muted-foreground"
                      }>{e.message}</span>
                    </div>
                  );
                })}
                {(!logs[selectedBot.id] && !selectedBot.log?.length) && <div className="text-xs text-muted-foreground italic">Waiting for activity...</div>}
              </div>
            </Card>
          ) : (
            <Card className="p-5 border-border rounded-xl">
              <div className="text-xs uppercase text-muted-foreground mb-2">Tip</div>
              <div className="text-sm text-muted-foreground">Select a bot to see live activity.</div>
            </Card>
          )}
        </div>
      </div>

      {showBuilder && <BotBuilder onClose={() => { setShowBuilder(false); load(); }} />}
      {celebration && (
        <TierCelebration
          tier={celebration.tier}
          botName={celebration.botName}
          onClose={() => setCelebration(null)}
        />
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className="font-medium capitalize truncate" title={value}>{value}</div>
  </div>
);

function BotBuilder({ onClose }) {
  const [strategy, setStrategy] = useState("dca");
  const [name, setName] = useState("");
  const [interval, setInt] = useState(15);
  const [durationType, setDurationType] = useState("count");
  const [durationValue, setDurationValue] = useState(5);
  const [route, setRoute] = useState("sim");
  const [maxPerTrade, setMaxPerTrade] = useState(200);
  const [maxTotal, setMaxTotal] = useState(2000);
  const [maxDrawdown, setMaxDrawdown] = useState(10);
  const [stopLossPct, setStopLossPct] = useState("5");
  const [takeProfitPct, setTakeProfitPct] = useState("10");
  const [trailingStop, setTrailingStop] = useState(false);
  const [minVolume, setMinVolume] = useState("");
  const [symbols, setSymbols] = useState(
    CRYPTO_SEED.slice(0, 2).map((s) => ({ symbol: s, asset_type: "crypto" }))
    .concat(STOCK_SEED.slice(0, 2).map((s) => ({ symbol: s, asset_type: "stock" })))
  );
  const [thresholdPct, setThresholdPct] = useState(1);
  const [stepPct, setStepPct] = useState(2);
  const [patternPicks, setPatternPicks] = useState(["Bullish Engulfing", "Hammer", "ABCD (bull)"]);
  const [submitting, setSubmitting] = useState(false);
  const [available, setAvailable] = useState({ crypto: [], stocks: [] });
  const [customSym, setCustomSym] = useState("");
  const [customType, setCustomType] = useState("stock");
  const [customAdding, setCustomAdding] = useState(false);

  // Add any ticker to the basket by typing it — Aanar verifies the symbol
  // resolves to real market data before adding it to the selection.
  const addCustom = async (e) => {
    e && e.preventDefault();
    const raw = customSym.trim();
    if (!raw) return;
    if (!/^[a-zA-Z0-9.\-]{1,16}$/.test(raw)) {
      toast.error("Ticker must be 1-16 letters/numbers");
      return;
    }
    const finalSym = customType === "crypto" ? raw.toLowerCase() : raw.toUpperCase();
    if (symbols.some((s) => s.symbol === finalSym)) {
      toast.info(`${finalSym} is already in the basket`);
      setCustomSym("");
      return;
    }
    setCustomAdding(true);
    let verified = null;
    try {
      const { data } = await api.get(`/assets/${customType}/${finalSym}`);
      if (data && (data.price != null || data.name)) verified = data;
    } catch { /* add anyway — thin-liquidity tickers may 404 briefly */ }
    setSymbols((cur) => [...cur, { symbol: finalSym, asset_type: customType }]);
    toast.success(`Added ${finalSym.toUpperCase()} to basket`, {
      description: verified?.name ? `${verified.name} · ${fmtMoney(verified.price)}` : "Price will populate at bot start.",
    });
    setCustomSym("");
    setCustomAdding(false);
  };

  useEffect(() => {
    Promise.allSettled([api.get("/markets/crypto"), api.get("/markets/stocks")]).then(([c, s]) => {
      setAvailable({
        crypto: c.status === "fulfilled" ? c.value.data : [],
        stocks: s.status === "fulfilled" ? s.value.data : [],
      });
    });
  }, []);

  const toggle = (symbol, asset_type) => {
    setSymbols((cur) => {
      const found = cur.find((x) => x.symbol === symbol);
      return found ? cur.filter((x) => x.symbol !== symbol) : [...cur, { symbol, asset_type }];
    });
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Name your bot");
    if (strategy !== "ai" && symbols.length === 0) return toast.error("Pick at least one asset");
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(), strategy,
        strategy_params: strategy === "momentum" ? { threshold_pct: parseFloat(thresholdPct) }
                        : strategy === "grid" ? { step_pct: parseFloat(stepPct) }
                        : strategy === "pattern" ? { patterns: patternPicks } : {},
        symbols,
        duration_type: durationType,
        duration_value: parseFloat(durationValue) || 0,
        interval_seconds: parseInt(interval),
        route,
        safety: {
          max_per_trade: parseFloat(maxPerTrade) || 0,
          max_total: parseFloat(maxTotal) || 0,
          max_drawdown_pct: parseFloat(maxDrawdown) || 0,
          stop_loss_pct: parseFloat(stopLossPct) || 0,
          take_profit_pct: parseFloat(takeProfitPct) || 0,
          trailing_stop: trailingStop,
          ...(minVolume === "" ? {} : { min_volume_usd_24h: Math.max(0, parseFloat(minVolume) || 0) }),
        },
      };
      await api.post("/bots", payload);
      toast.success("Bot started");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-3xl w-full max-h-[92vh] overflow-y-auto border-border rounded-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="bot-builder">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Configure</div>
            <div className="font-display text-2xl font-bold">New auto-trading bot</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="builder-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <Label>Bot name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Weekly BTC DCA" data-testid="bot-name" className="mt-1" />
          </div>

          <div>
            <Label>Strategy</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
              {STRATEGIES.map((s) => (
                <button key={s.v} onClick={() => setStrategy(s.v)}
                        data-testid={`strategy-${s.v}`}
                        className={`text-left p-3 rounded-lg border transition-colors ${strategy === s.v ? "border-foreground bg-secondary/50" : "border-border hover:border-foreground/40"}`}>
                  <div className="flex items-center gap-2">
                    <s.icon className="w-4 h-4" />
                    <div className="font-medium text-sm">{s.label}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{s.blurb}</div>
                </button>
              ))}
            </div>
          </div>

          {(strategy === "momentum") && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Threshold %</Label>
                <Input type="number" min="0.1" step="0.1" value={thresholdPct} onChange={(e) => setThresholdPct(e.target.value)}
                       data-testid="param-threshold" className="mt-1 font-mono-tab" />
                <div className="text-[11px] text-muted-foreground mt-1">Trade only if 24h |change| ≥ this</div>
              </div>
            </div>
          )}
          {(strategy === "grid") && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Grid step %</Label>
                <Input type="number" min="0.5" step="0.5" value={stepPct} onChange={(e) => setStepPct(e.target.value)}
                       data-testid="param-step" className="mt-1 font-mono-tab" />
                <div className="text-[11px] text-muted-foreground mt-1">Buy after dip, sell after rise of this size</div>
              </div>
            </div>
          )}

          {strategy === "pattern" && (
            <div>
              <Label>Patterns to trade off of</Label>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="pattern-picks">
                {PATTERNS.map((p) => {
                  const on = patternPicks.includes(p.v);
                  return (
                    <button key={p.v} type="button"
                            onClick={() => setPatternPicks((cur) => on ? cur.filter((x) => x !== p.v) : [...cur, p.v])}
                            data-testid={`pattern-${p.v.replace(/\s+/g, '-').toLowerCase()}`}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? (p.kind === "bull" ? "bg-[hsl(var(--gain))]/20 border-[hsl(var(--gain))] gain" : p.kind === "bear" ? "bg-[hsl(var(--loss))]/20 border-[hsl(var(--loss))] loss" : "bg-[hsl(var(--ai))]/20 border-[hsl(var(--ai))]") : "border-border hover:border-foreground/40 text-muted-foreground"}`}>
                      {p.v}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">{patternPicks.length} selected. The bot enters only when one of these signals fires.</div>
            </div>
          )}

          <div>
            <Label>Asset basket {strategy === "ai" && <span className="text-muted-foreground text-xs font-normal">(AI picks from this universe)</span>}</Label>
            <div className="mt-2 space-y-3">
              {/* Type any ticker to add it to the basket */}
              <form onSubmit={addCustom} className="flex items-center gap-2" data-testid="bot-basket-add">
                <div className="flex gap-1">
                  <button type="button" onClick={() => setCustomType("stock")}
                          data-testid="bot-basket-type-stock"
                          className={`px-2.5 py-1 rounded-full text-[10px] font-mono-tab uppercase tracking-wider border transition-colors ${
                            customType === "stock"
                              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                              : "bg-secondary/30 text-muted-foreground border-transparent"
                          }`}>Stock</button>
                  <button type="button" onClick={() => setCustomType("crypto")}
                          data-testid="bot-basket-type-crypto"
                          className={`px-2.5 py-1 rounded-full text-[10px] font-mono-tab uppercase tracking-wider border transition-colors ${
                            customType === "crypto"
                              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                              : "bg-secondary/30 text-muted-foreground border-transparent"
                          }`}>Crypto</button>
                </div>
                <Input
                  value={customSym}
                  onChange={(e) => setCustomSym(e.target.value.toUpperCase())}
                  placeholder={customType === "crypto" ? "e.g. bitcoin, solana" : "Type any ticker (e.g. SHAZ, PLTR, BRK.B)"}
                  data-testid="bot-basket-input"
                  maxLength={16}
                  autoComplete="off"
                  className="flex-1 h-8 text-xs font-mono-tab uppercase bg-secondary/30 border-border/60"
                />
                <button type="submit" disabled={customAdding || !customSym.trim()}
                        data-testid="bot-basket-add-btn"
                        className="h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-[11px] font-bold flex items-center gap-1">
                  <Plus className="w-3 h-3" /> {customAdding ? "…" : "Add"}
                </button>
              </form>

              {/* Selected symbols — shows both preset picks AND custom-added tickers */}
              {symbols.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-muted-foreground mb-1">Selected ({symbols.length})</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="bot-basket-selected">
                    {symbols.map((s) => (
                      <button key={s.symbol}
                              onClick={() => toggle(s.symbol, s.asset_type)}
                              data-testid={`chip-selected-${s.symbol}`}
                              className="px-2.5 py-1 rounded-full text-xs border bg-emerald-500/20 border-emerald-500/40 text-emerald-100 flex items-center gap-1 hover:bg-emerald-500/30">
                        {s.symbol.toUpperCase()}
                        <X className="w-3 h-3 opacity-70" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Quick-add crypto</div>
                <div className="flex flex-wrap gap-1.5">
                  {available.crypto.slice(0, 8).map((a) => {
                    const on = symbols.some((s) => s.symbol === a.symbol);
                    return (
                      <button key={a.symbol}
                              onClick={() => toggle(a.symbol, "crypto")}
                              data-testid={`chip-${a.symbol}`}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
                        {a.ticker}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Quick-add stocks</div>
                <div className="flex flex-wrap gap-1.5">
                  {available.stocks.slice(0, 8).map((a) => {
                    const on = symbols.some((s) => s.symbol === a.symbol);
                    return (
                      <button key={a.symbol}
                              onClick={() => toggle(a.symbol, "stock")}
                              data-testid={`chip-${a.symbol}`}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"}`}>
                        {a.ticker}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Duration</Label>
              <Tabs value={durationType} onValueChange={setDurationType} className="mt-1">
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="count" data-testid="dur-count">Count</TabsTrigger>
                  <TabsTrigger value="time" data-testid="dur-time">Time</TabsTrigger>
                  <TabsTrigger value="manual" data-testid="dur-manual">Manual</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {durationType !== "manual" && (
              <div>
                <Label>{durationType === "count" ? "Number of trades" : "Time (seconds)"}</Label>
                <Input type="number" min="1" value={durationValue} onChange={(e) => setDurationValue(e.target.value)}
                       data-testid="dur-value" className="mt-1 font-mono-tab" />
              </div>
            )}
            <div>
              <Label>Interval (seconds)</Label>
              <Input type="number" min="5" max="3600" value={interval} onChange={(e) => setInt(e.target.value)}
                     data-testid="interval" className="mt-1 font-mono-tab" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Broker route</Label>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {route === "sim" && "Paper · full auto"}
                {route === "ibkr_paper" && "IBKR paper · full auto"}
                {route === "ibkr_live" && "IBKR live · full auto"}
                {route === "ibkr_mcp" && "IBKR live · 1-tap confirm"}
                {route === "current" && "Follows IBKR connection"}
              </span>
            </div>
            <Tabs value={route} onValueChange={setRoute} className="mt-1">
              <TabsList className="grid grid-cols-5 w-full">
                <TabsTrigger value="sim" data-testid="route-sim">Sim</TabsTrigger>
                <TabsTrigger value="ibkr_paper" data-testid="route-ibkr-paper">IBKR Paper</TabsTrigger>
                <TabsTrigger value="ibkr_live" data-testid="route-ibkr-live">IBKR Live</TabsTrigger>
                <TabsTrigger value="ibkr_mcp" data-testid="route-ibkr-mcp">IBKR · 1-tap</TabsTrigger>
                <TabsTrigger value="current" data-testid="route-current">Current</TabsTrigger>
              </TabsList>
            </Tabs>
            {route === "ibkr_mcp" && (
              <div className="mt-2 text-[11px] text-muted-foreground leading-relaxed p-2 rounded-lg bg-secondary/40 border border-border/50">
                Aanar drafts every trade into your IBKR "AI Instructions" tab.
                You 1-tap confirm inside IBKR — safest live mode. Requires an
                IBKR MCP connection in Settings.
              </div>
            )}
          </div>

          <div>
            <Label>Exit rules · applied to every buy</Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase text-muted-foreground">
                    {trailingStop ? "Trailing stop %" : "Stop-loss %"}
                  </div>
                  <button type="button"
                          onClick={() => setTrailingStop(!trailingStop)}
                          data-testid="safety-trailing-toggle"
                          className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-mono-tab transition-colors ${
                            trailingStop
                              ? "bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))] border border-[hsl(var(--ai))]/40"
                              : "bg-secondary/50 text-muted-foreground border border-border/50 hover:text-foreground"
                          }`}>
                    {trailingStop ? "Trailing ✓" : "Fixed"}
                  </button>
                </div>
                <Input type="number" min="0" max="50" step="0.5" value={stopLossPct}
                       onChange={(e) => setStopLossPct(e.target.value)}
                       data-testid="safety-stop-loss-pct" className="mt-1 font-mono-tab" placeholder="0 = off" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {trailingStop
                    ? "Stop follows price up; sells if it drops this % below the peak"
                    : "Auto-sell if price drops this % below entry"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">Take-profit %</div>
                <Input type="number" min="0" max="500" step="0.5" value={takeProfitPct}
                       onChange={(e) => setTakeProfitPct(e.target.value)}
                       data-testid="safety-take-profit-pct" className="mt-1 font-mono-tab" placeholder="0 = off" />
                <div className="text-[10px] text-muted-foreground mt-1">Auto-sell if price rises this % above entry</div>
              </div>
            </div>
          </div>

          <div>
            <Label>Safety guards</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">Max $ / trade</div>
                <Input type="number" min="0" value={maxPerTrade} onChange={(e) => setMaxPerTrade(e.target.value)}
                       data-testid="safety-per-trade" className="mt-1 font-mono-tab" />
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">Max $ total</div>
                <Input type="number" min="0" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)}
                       data-testid="safety-total" className="mt-1 font-mono-tab" />
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">Stop at DD %</div>
                <Input type="number" min="0" max="50" value={maxDrawdown} onChange={(e) => setMaxDrawdown(e.target.value)}
                       data-testid="safety-dd" className="mt-1 font-mono-tab" />
              </div>
              <div>
                <div className="text-[11px] uppercase text-muted-foreground">Min 24h $ vol</div>
                <Input type="number" min="0" step="100000" placeholder="Off" value={minVolume}
                       onChange={(e) => setMinVolume(e.target.value)}
                       data-testid="safety-min-volume" className="mt-1 font-mono-tab" />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">Empty = use your global default from Settings.</div>
          </div>
        </div>
        <div className="p-6 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="bot-start"
                  className="rounded-full bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black">
            <Play className="w-4 h-4 mr-2" /> {submitting ? "Starting..." : "Start bot"}
          </Button>
        </div>
      </Card>
    </div>
  );
}


function TierProgress({ botId }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get(`/bots/${botId}/tier`).then((r) => setT(r.data)).catch(() => {});
  }, [botId]);
  if (!t) return null;
  const colors = {
    platinum: "from-slate-200 to-slate-400",
    gold: "bg-amber-500/90 text-amber-950",
    silver: "bg-slate-400/80 text-slate-900",
    bronze: "bg-orange-800/70 text-orange-100",
  };
  return (
    <div className="mb-3 p-2 rounded-lg bg-secondary/30 border border-border/60" onClick={(e) => e.stopPropagation()} data-testid={`tier-progress-${botId}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-xs">
          {t.tier ? (
            <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${
              t.tier === "platinum" ? `bg-gradient-to-r ${colors.platinum} text-slate-900` : colors[t.tier]
            }`}>{t.tier}</span>
          ) : (
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">unranked</span>
          )}
          <span className="text-muted-foreground">→ {t.next_tier || "top tier"}</span>
        </div>
        <div className="text-[10px] font-mono-tab text-muted-foreground">{t.progress_pct}%</div>
      </div>
      <div className="w-full h-1 rounded-full bg-border overflow-hidden">
        <div className="h-full bg-[hsl(var(--gain))] rounded-full transition-all" style={{ width: `${t.progress_pct}%` }} />
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5">{t.progress_hint}</div>
    </div>
  );
}
